import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { loadBase, loadDefensiveStats, loadAdvancedStats } from '../../services/espnService';
import { useConfig, CONFIG_DEFAULTS } from '../../hooks/useConfig';

const DEF_FORMULAS = {
  DE: ['sacks', 'QBHits', 'tacklesForLoss', 'totalTackles', 'forcedFumbles', 'prss', 'hrry'],
  DT: ['sacks', 'QBHits', 'tacklesForLoss', 'totalTackles', 'forcedFumbles', 'prss', 'hrry'],
  LB: ['totalTackles', 'sacks', 'tacklesForLoss', 'passesDefended', 'forcedFumbles', 'prss', '=~m_tkl_percent'],
  CB: ['passesDefended', 'interceptions', 'totalTackles', '=~yds_tgt', '=~m_tkl_percent'],
  S:  ['totalTackles', 'passesDefended', 'interceptions', '=~yds_tgt', '=~m_tkl_percent'],
};

function parseKey(key) {
  let k = key;
  const isRaw     = k.startsWith('='); if (isRaw)     k = k.slice(1);
  const isPenalty = k.startsWith('~'); if (isPenalty) k = k.slice(1);
  return { rawKey: k, isRaw, isPenalty };
}

const MIN_GAMES = 6;
const POSITIONS = ['DE', 'DT', 'LB', 'CB', 'S'];
const POS_SLOTS = { DE: 2, DT: 2, LB: 3, CB: 2, S: 2 };
const DEF_POS_CAPS = { DE: 65, DT: 65, LB: 97, CB: 65, S: 65 };

const TEAM_NAMES = {
  ARI:'Cardinals', ATL:'Falcons', BAL:'Ravens', BUF:'Bills',
  CAR:'Panthers', CHI:'Bears', CIN:'Bengals', CLE:'Browns',
  DAL:'Cowboys', DEN:'Broncos', DET:'Lions', GB:'Packers',
  HOU:'Texans', IND:'Colts', JAX:'Jaguars', KC:'Chiefs',
  LAC:'Chargers', LAR:'Rams', LV:'Raiders', MIA:'Dolphins',
  MIN:'Vikings', NE:'Patriots', NO:'Saints', NYG:'Giants',
  NYJ:'Jets', PHI:'Eagles', PIT:'Steelers', SEA:'Seahawks',
  SF:'49ers', TB:'Buccaneers', TEN:'Titans', WSH:'Commanders',
};

function computePosNR(pos, players, defStats, weights = {}) {
  const statKeys = DEF_FORMULAS[pos];

  const eligible = players.filter(p => {
    const s = defStats[p.id];
    return s && s.gamesPlayed >= MIN_GAMES;
  }).map(p => {
    const s = defStats[p.id];
    const gp = s.gamesPlayed;
    const perGame = {};
    statKeys.forEach(k => {
      const { rawKey, isRaw } = parseKey(k);
      const val = s[rawKey] ?? 0;
      perGame[k] = isRaw ? val : (gp > 0 ? val / gp : 0);
    });
    return { ...p, perGame, gamesPlayed: gp };
  });

  const maxRank = Math.min(eligible.length + 1, DEF_POS_CAPS[pos] ?? 65);

  if (!eligible.length) {
    const nrMap = {};
    players.forEach(p => { nrMap[p.id] = maxRank; });
    return { nrMap, maxRank };
  }

  const statRanks = {};
  eligible.forEach(p => { statRanks[p.id] = {}; });

  statKeys.forEach(statKey => {
    const { isPenalty } = parseKey(statKey);
    const sorted = [...eligible].sort((a, b) =>
      isPenalty ? a.perGame[statKey] - b.perGame[statKey] : b.perGame[statKey] - a.perGame[statKey]
    );
    let rank = 1, i = 0;
    while (i < sorted.length) {
      const tieVal = sorted[i].perGame[statKey];
      let j = i;
      while (j < sorted.length && sorted[j].perGame[statKey] === tieVal) {
        statRanks[sorted[j].id][statKey] = rank;
        j++;
      }
      rank += j - i;
      i = j;
    }
  });

  const withNR = eligible.map(p => {
    const rankSum = statKeys.reduce((acc, k) => {
      const w = weights[k] ?? 1;
      return acc + (statRanks[p.id][k] ?? eligible.length) * w;
    }, 0);
    return { ...p, rankSum };
  }).sort((a, b) => a.rankSum - b.rankSum);

  const nrMap = {};
  let nr = 1, i = 0;
  while (i < withNR.length) {
    const tieSum = withNR[i].rankSum;
    let j = i;
    while (j < withNR.length && withNR[j].rankSum === tieSum) {
      nrMap[withNR[j].id] = nr;
      j++;
    }
    nr += j - i;
    i = j;
  }

  players.forEach(p => {
    if (!(p.id in nrMap)) nrMap[p.id] = maxRank;
  });

  return { nrMap, maxRank };
}

// Fetch team-level points allowed per game from ESPN teams API
async function fetchTeamScoringStats() {
  try {
    const res  = await fetch('https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams?limit=32');
    const data = await res.json();
    const teamList = data?.sports?.[0]?.leagues?.[0]?.teams || [];
    const ids = teamList.map(t => t.team.id);
    const teams = await Promise.all(ids.map(id =>
      fetch(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${id}`)
        .then(r => r.json())
        .then(d => d?.team || null)
        .catch(() => null)
    ));
    const map = {};
    teams.filter(Boolean).forEach(t => {
      const rec   = (t.record?.items || []).find(i => i.type === 'total') || {};
      const stats = rec.stats || [];
      const getStat = (arr, name) => arr.find(s => s.name === name)?.value ?? null;
      const wins   = getStat(stats, 'wins') ?? 0;
      const losses = getStat(stats, 'losses') ?? 0;
      const ties   = getStat(stats, 'ties') ?? 0;
      const games  = wins + losses + ties || null;
      const pa     = getStat(stats, 'pointsAgainst') ?? null;
      map[t.abbreviation] = { pa, games, paPg: (pa !== null && games) ? pa / games : null };
    });
    return map;
  } catch {
    return {};
  }
}

// ── Styles ────────────────────────────────────────────────────────────────────
const cellSx = {
  color: '#9eb8d0', fontSize: '0.75rem', borderColor: 'rgba(30,52,72,0.5)', py: 0.5, px: 0.75,
};
const headerCellSx = {
  color: '#f5a623', fontWeight: 700, fontSize: '0.7rem', borderColor: 'rgba(30,52,72,0.5)',
  py: 0.75, px: 1, backgroundColor: '#060e1a',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function DefensiveTeamNR() {
  const { config } = useConfig();
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [players, setPlayers]         = useState({});
  const [defStats, setDefStats]       = useState({});
  const [teamScoring, setTeamScoring] = useState({});

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadBase().then(({ players: p }) => {
        const defPlayers = {};
        Object.values(p).forEach(pl => {
          if (POSITIONS.includes(pl.posKey)) defPlayers[pl.id] = pl;
        });
        return loadDefensiveStats(Object.keys(defPlayers)).then(s => ({ defPlayers, s }));
      }),
      fetchTeamScoringStats(),
      loadAdvancedStats(),
    ])
      .then(([{ defPlayers, s }, scoring, advS]) => {
        // Merge advanced stats into defensive stats by ESPN player ID (additive)
        const merged = { ...s };
        Object.entries(advS).forEach(([espnId, adv]) => {
          if (merged[espnId]) merged[espnId] = { ...merged[espnId], ...adv };
        });
        setPlayers(defPlayers);
        setDefStats(merged);
        setTeamScoring(scoring);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load defensive player data.'); setLoading(false); });
  }, []);

  const ptsWeight = config?.nr_weights_team_def?.ptsWeight
    ?? CONFIG_DEFAULTS.nr_weights_team_def?.ptsWeight
    ?? 1.5;

  const teamRankings = useMemo(() => {
    if (!Object.keys(players).length || !Object.keys(defStats).length) return [];

    const posData = {};
    POSITIONS.forEach(pos => {
      const posPlayers = Object.values(players).filter(p => p.posKey === pos);
      const weights    = config[`nr_weights_def_${pos.toLowerCase()}`] ?? {};
      const { nrMap, maxRank } = computePosNR(pos, posPlayers, defStats, weights);
      posData[pos] = { nrMap, maxRank, players: posPlayers };
    });

    const teamMap = {};
    Object.values(players).forEach(p => {
      if (!teamMap[p.team]) teamMap[p.team] = { DE: [], DT: [], LB: [], CB: [], S: [] };
      if (POSITIONS.includes(p.posKey)) teamMap[p.team][p.posKey].push(p);
    });

    const rows = Object.entries(teamMap).map(([team, byPos]) => {
      const posAvg     = {};
      const posPlayers = {};

      POSITIONS.forEach(pos => {
        const { nrMap, maxRank } = posData[pos];
        const slots = POS_SLOTS[pos] ?? 1;
        const teamPosPlayers = byPos[pos] || [];
        const playerNRs = teamPosPlayers.map(p => ({ name: p.shortName || p.name, nr: nrMap[p.id] ?? maxRank }));
        playerNRs.sort((a, b) => a.nr - b.nr);
        const topN   = playerNRs.slice(0, slots);
        const filled = [...topN];
        while (filled.length < slots) filled.push({ name: '—', nr: maxRank });
        posAvg[pos]     = filled.reduce((s, p) => s + p.nr, 0) / slots;
        posPlayers[pos] = playerNRs;
      });

      const posScore = POSITIONS.reduce((s, pos) => s + posAvg[pos], 0) / POSITIONS.length;
      const scoring  = teamScoring[team] ?? {};

      return { team, posScore, posContrib: posAvg, posPlayers, paPg: scoring.paPg ?? null };
    });

    // Blend points-allowed rank into total score (lower pa = better = lower rank)
    if (Object.keys(teamScoring).length) {
      const withPa = rows.filter(r => r.paPg !== null);
      const sorted  = [...withPa].sort((a, b) => a.paPg - b.paPg); // fewer pts allowed = better
      const rankMap = {};
      sorted.forEach((r, i) => { rankMap[r.team] = i + 1; });
      const maxPaRank = sorted.length + 1;
      const posCount  = POSITIONS.length;
      rows.forEach(r => {
        const paRank    = rankMap[r.team] ?? maxPaRank;
        r.paRank        = paRank;
        r.totalScore    = (r.posScore * posCount + paRank * ptsWeight) / (posCount + ptsWeight);
      });
    } else {
      rows.forEach(r => { r.paRank = null; r.totalScore = r.posScore; });
    }

    rows.sort((a, b) => a.totalScore - b.totalScore);
    return rows.map((r, i) => ({ ...r, teamNR: i + 1 }));
  }, [players, defStats, config, teamScoring, ptsWeight]); // eslint-disable-line

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#f5a623' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Computing team defensive ratings…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ color: '#ef4444' }}>{error}</Typography>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8' }}>
          Team Defensive Nerd Rating
        </Typography>
        <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
      </Box>
      <Typography variant="caption" sx={{ color: '#6a8aa8', display: 'block', mb: 2.5, lineHeight: 1.6 }}>
        Average NR of top starters per position (DE1+2 · DT1+2 · LB1+2+3 · CB1+2 · S1+2), blended with points-allowed rank.
        PA/G rank weight is adjustable in Admin. Lower total score = stronger defense.
      </Typography>

      <TableContainer component={Paper} sx={{ backgroundColor: '#060e1a', border: '1px solid rgba(30,52,72,0.6)', borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 48 }}>NR</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 130 }}>Team</TableCell>
              <TableCell align="center" sx={{ ...headerCellSx, width: 72 }}>Score ↓</TableCell>
              <TableCell align="center" sx={{ ...headerCellSx, width: 80 }}>PA/G</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top DE <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top DT <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top LB <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top CB <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top S <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamRankings.map((row, idx) => (
              <TableRow key={row.team} sx={{
                backgroundColor: idx % 2 === 0 ? 'rgba(6,14,26,0.5)' : 'rgba(10,20,38,0.4)',
                '&:hover': { backgroundColor: 'rgba(22,44,70,0.6)' },
              }}>
                {/* Rank */}
                <TableCell sx={{
                  ...cellSx, fontWeight: 900, width: 48, fontSize: '0.82rem',
                  color: row.teamNR <= 5 ? '#2ea84b' : row.teamNR <= 10 ? '#f5a623' : '#e8f0f8',
                }}>
                  #{row.teamNR}
                </TableCell>

                {/* Team name */}
                <TableCell sx={{ ...cellSx, width: 130 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#e8f0f8', lineHeight: 1.2 }}>
                    {TEAM_NAMES[row.team] ?? row.team}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#4a6080', lineHeight: 1.2 }}>
                    {row.team}
                  </Typography>
                </TableCell>

                {/* Total score */}
                <TableCell align="center" sx={{ ...cellSx, fontWeight: 700, color: '#e8f0f8', width: 72 }}>
                  {row.totalScore.toFixed(1)}
                </TableCell>

                {/* PA/G */}
                <TableCell align="center" sx={{ ...cellSx, width: 80 }}>
                  {row.paPg != null ? (
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#c0d4e8', fontFamily: 'monospace' }}>
                        {row.paPg.toFixed(1)}
                      </Typography>
                      {row.paRank != null && (
                        <Typography sx={{ fontSize: '0.58rem', color: '#4a6080' }}>#{row.paRank}</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.7rem', color: '#3a5570' }}>—</Typography>
                  )}
                </TableCell>

                {/* Top player + avg per position */}
                {POSITIONS.map(pos => {
                  const top = row.posPlayers[pos]?.[0];
                  const avg = row.posContrib[pos];
                  const cap = DEF_POS_CAPS[pos] ?? 65;
                  return (
                    <TableCell key={pos} sx={{ ...cellSx }}>
                      {top ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '0.73rem', color: '#c0d4e8' }}>{top.name}</Typography>
                          <Chip
                            label={`#${top.nr}`}
                            size="small"
                            sx={{
                              fontSize: '0.58rem', height: 16, fontWeight: 700,
                              bgcolor: top.nr <= 5 ? 'rgba(245,166,35,0.15)' : 'rgba(255,255,255,0.05)',
                              color: top.nr <= 5 ? '#f5a623' : '#6a8aa8',
                              border: `1px solid ${top.nr <= 5 ? 'rgba(245,166,35,0.3)' : 'rgba(255,255,255,0.08)'}`,
                            }}
                          />
                          {avg != null && (
                            <Typography sx={{ fontSize: '0.62rem', color: '#4a6080' }}>
                              avg {avg.toFixed(1)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontSize: '0.7rem', color: '#3a5570' }}>No {pos}</Typography>
                          <Chip
                            label={`#${cap}`}
                            size="small"
                            sx={{
                              fontSize: '0.58rem', height: 16, fontWeight: 700,
                              bgcolor: 'rgba(255,255,255,0.03)', color: '#3a5570',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}
                          />
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
